import { c as _c } from "react/compiler-runtime";
import { ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import type { StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementOptions, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { PaymentMethods } from 'api-graphql-types';
import ApplePayLogo from 'assets/images/logos/applepay.svg';
import GooglePayLogo from 'assets/images/logos/googlepay.svg';
import classNames from 'classnames';
import { useIsMounted } from 'design-system/client';
import { Icon } from 'design-system/server';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import useIsFormValid from '../useIsFormValid';
import styles from './StripeNativePaymentButton.module.css';
import useDelayLoadingNativePaymentMethod from './useDelayLoadingNativePaymentMethod';
import useFormSubmit from './useFormSubmit';
import { useDonationFormContext, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import { type NativePaymentMethod } from '@/donationPages/_dependencies/NativePaymentMethods';
const buttonHeight = 48;
const logoMap = {
  [PaymentMethods.StripeApplePay]: ApplePayLogo as string,
  [PaymentMethods.StripeGooglePay]: GooglePayLogo as string
};
function PaymentInProgressButton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classNames(styles.loadingButtonWrapper);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      height: buttonHeight + 1
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={t0}><button className={styles.button} style={t1} disabled={true} onClick={_temp} type="button"><Icon name="spinner" style={{
          opacity: 1
        }} /></button></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(e) {
  return e.preventDefault();
}
function DefaultDummyButton(t0) {
  const $ = _c(10);
  const {
    onClick,
    disabled,
    paymentMethod,
    ready
  } = t0;
  const t = useTranslations();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classNames(styles.wrapper, styles.dummy);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      height: buttonHeight + 1
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== paymentMethod || $[3] !== ready || $[4] !== t) {
    t3 = !ready && <span className={styles.readyButtonContent}><Icon name="spinner" style={{
        fill: "white"
      }} size="400" /><Image style={{
        display: "inline-block"
      }} src={logoMap[paymentMethod]} alt={t("nextjs.core.submit")} /></span>;
    $[2] = paymentMethod;
    $[3] = ready;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== disabled || $[7] !== onClick || $[8] !== t3) {
    t4 = <div className={t1}><button className={styles.button} style={t2} disabled={disabled} onClick={onClick} type="submit">{t3}</button></div>;
    $[6] = disabled;
    $[7] = onClick;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function DummyStripeNativePaymentButton(t0) {
  const $ = _c(9);
  const {
    onClick: onClick_,
    isSubmitting,
    ready,
    paymentMethod
  } = t0;
  const valid = useIsFormValid();
  const disabled = !useIsMounted();
  let t1;
  if ($[0] !== onClick_ || $[1] !== valid) {
    t1 = () => {
      if (!valid) {
        return;
      }
      onClick_?.();
    };
    $[0] = onClick_;
    $[1] = valid;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onClick = t1;
  if (isSubmitting) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <PaymentInProgressButton />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== disabled || $[5] !== onClick || $[6] !== paymentMethod || $[7] !== ready) {
    t2 = <DefaultDummyButton onClick={onClick} disabled={disabled} paymentMethod={paymentMethod} ready={ready} />;
    $[4] = disabled;
    $[5] = onClick;
    $[6] = paymentMethod;
    $[7] = ready;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function getStripePaymentOptions(paymentMethod: NativePaymentMethod): StripeExpressCheckoutElementOptions {
  const googlePayKey = 'googlePay';
  const applePayKey = 'applePay';
  const activeKey = paymentMethod === PaymentMethods.StripeApplePay ? applePayKey : googlePayKey;
  const inactiveKey = paymentMethod === PaymentMethods.StripeApplePay ? googlePayKey : applePayKey;
  return {
    paymentMethods: {
      [activeKey]: 'always',
      [inactiveKey]: 'never'
    },
    buttonType: {
      [activeKey]: 'donate'
    },
    buttonHeight,
    layout: {
      maxColumns: 1,
      maxRows: 0,
      overflow: 'never'
    },
    buttonTheme: {
      [activeKey]: 'black'
    }
  };
}
function useButtonOptions(paymentMethod) {
  const $ = _c(2);
  let t0;
  if ($[0] !== paymentMethod) {
    t0 = getStripePaymentOptions(paymentMethod);
    $[0] = paymentMethod;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function UpdateStripeFromTotal() {
  const $ = _c(5);
  const elements = useElements();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["amount_cents", "codonation_cents"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [amount, codonation] = useDonationFormValues(t0);
  const total = amount + codonation;
  let t1;
  let t2;
  if ($[1] !== elements || $[2] !== total) {
    t1 = () => {
      if (!elements) {
        return;
      }
      if (!total) {
        return;
      }
      elements.update({
        amount: total
      });
    };
    t2 = [elements, total];
    $[1] = elements;
    $[2] = total;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}
function StripeNativePaymentButtonInner(t0) {
  const $ = _c(31);
  const {
    paymentMethod,
    onClick: onClick_
  } = t0;
  const {
    onConfirm,
    displayError
  } = useFormSubmit();
  const [ready, setReady] = useState(false);
  const {
    formState: t1
  } = useDonationFormContext();
  const {
    isSubmitting
  } = t1;
  const valid = useIsFormValid();
  const disabled = isSubmitting || !valid;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = _ => {
      setReady(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const onReady = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      setReady(false);
    };
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== onClick_) {
    t5 = event => {
      onClick_?.();
      event.resolve();
    };
    $[3] = onClick_;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const onClick = t5;
  const loading = useDelayLoadingNativePaymentMethod();
  const options = useButtonOptions(paymentMethod);
  let t6;
  if ($[5] !== displayError) {
    t6 = displayError && <p className={styles.errorMessage}>{displayError}</p>;
    $[5] = displayError;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <UpdateStripeFromTotal />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = ready && !disabled ? "auto" : "none";
  let t9;
  if ($[8] !== t8) {
    t9 = {
      pointerEvents: t8
    };
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = !ready || disabled;
  let t11;
  if ($[10] !== loading || $[11] !== onClick || $[12] !== onConfirm || $[13] !== options) {
    t11 = !loading && <ExpressCheckoutElement options={options} onReady={onReady} onConfirm={onConfirm} onClick={onClick} onLoadError={error => {
      console.error(error);
      setReady(false);
    }} />;
    $[10] = loading;
    $[11] = onClick;
    $[12] = onConfirm;
    $[13] = options;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t9) {
    t12 = <div className={styles.wrapper} style={t9} inert={t10}>{t11}</div>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== disabled || $[20] !== isSubmitting || $[21] !== onClick_ || $[22] !== paymentMethod || $[23] !== ready) {
    t13 = (!ready || disabled) && <DummyStripeNativePaymentButton paymentMethod={paymentMethod} isSubmitting={isSubmitting} onClick={onClick_} ready={ready} />;
    $[19] = disabled;
    $[20] = isSubmitting;
    $[21] = onClick_;
    $[22] = paymentMethod;
    $[23] = ready;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <div className={styles.container}>{t7}{t12}{t13}</div>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t6) {
    t15 = <>{t6}{t14}</>;
    $[28] = t14;
    $[29] = t6;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
const StripeNativePaymentButton = memo(StripeNativePaymentButtonInner);
export default StripeNativePaymentButton;